import React from 'react';
import { TrendingUp } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import FirebaseTimeRangeSelector from '../../components/analytics/FirebaseTimeRangeSelector';
import { formatNumber, formatDate } from '../../utils/analytics-helpers';
import './UserGrowthChart.css';

const UserGrowthChart = ({ analytics, fullSize = false }) => {
  const { dailyActivity } = analytics;

  // Format data for display
  const chartData = dailyActivity.map(day => ({
    ...day,
    formattedDate: new Date(day.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    date: day.date
  }));

  // Calculate trend
  const recentGames = dailyActivity.slice(-7).reduce((sum, d) => sum + d.games, 0);
  const previousGames = dailyActivity.slice(-14, -7).reduce((sum, d) => sum + d.games, 0);
  const trend = previousGames > 0
    ? ((recentGames - previousGames) / previousGames * 100).toFixed(1)
    : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#111827' }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: entry.color }} />
            <span style={{ fontSize: '14px', color: '#6B7280' }}>
              {entry.name}: <strong style={{ color: '#111827' }}>{formatNumber(entry.value)}</strong>
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`user-growth-chart ${fullSize ? 'full-size' : ''}`}>
      <div className="chart-header">
        <div>
          <h3>Daily Activity</h3>
          <div className="trend-indicator">
            <TrendingUp size={16} className={trend >= 0 ? 'positive' : 'negative'} />
            <span className={trend >= 0 ? 'positive' : 'negative'}>
              {Math.abs(trend)}% vs last week
            </span>
          </div>
        </div>
        <FirebaseTimeRangeSelector />
      </div>

      <ResponsiveContainer width="100%" height={fullSize ? 400 : 250}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorGames" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="formattedDate"
            tick={{ fill: '#6B7280', fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickFormatter={(value) => formatNumber(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
              fontFamily: 'var(--font-body)'
            }}
          />
          <Area
            type="monotone"
            dataKey="games"
            stroke="#3B82F6"
            strokeWidth={2}
            fill="url(#colorGames)"
            name="Games Played"
          />
        </AreaChart>
      </ResponsiveContainer>

      {fullSize && (
        <div className="growth-stats">
          <div className="growth-stat">
            <div className="stat-value">{formatNumber(recentGames)}</div>
            <div className="stat-label">Last 7 Days</div>
          </div>
          <div className="growth-stat">
            <div className="stat-value">{formatNumber(previousGames)}</div>
            <div className="stat-label">Previous 7 Days</div>
          </div>
          <div className="growth-stat">
            <div className="stat-value">
              {formatNumber((dailyActivity.reduce((sum, d) => sum + d.games, 0) / dailyActivity.length).toFixed(1))}
            </div>
            <div className="stat-label">Avg Per Day</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGrowthChart;
